import { Stack, Typography } from "@mui/material";
import { type FC, useCallback, useEffect, useMemo, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import type { INTERVAL } from "../../../utils/iso8601";
import {
  type CustomRepeatEnd,
  type CustomRepeatType,
  useNumberOfRepeats,
} from "../hooks/use-number-of-repeats";
import type { Event } from "../types";
import { CustomRepeatEnds } from "./custom-repeat-ends";
import { CustomRepeatEvery } from "./custom-repeat-every";
import { CustomRepeatPeriod } from "./custom-repeat-period";
import { CustomRepeatSumamry } from "./custom-repeat-summary";

export const CustomRepeat: FC<{ hasError: boolean }> = ({ hasError }) => {
  const [customRepeat, setCustomRepeat] = useState<CustomRepeatType>({
    every: 1,
    period: "day",
    end: { type: "never" },
  });

  const { control, getValues, setValue } = useFormContext<Event>();

  useWatch({ control, name: "date" });
  const startDate = getValues("date");

  const { loading, data } = useNumberOfRepeats(customRepeat, startDate);

  const repeat = useMemo(
    () =>
      customRepeat.every && customRepeat.period
        ? (`${customRepeat.every} ${customRepeat.period}` as INTERVAL)
        : undefined,
    [customRepeat.every, customRepeat.period],
  );

  useEffect(() => {
    if (loading) return;

    setValue("repeat", repeat);
    setValue("number_of_repeats", data?.number_of_repeats, { shouldValidate: true });
  }, [data, loading, setValue, repeat]);

  const handleCustomRepeatChange = useCallback((end: CustomRepeatEnd) => {
    setCustomRepeat((prev) => ({ ...prev, end }));
  }, []);

  return (
    <Stack
      direction="column"
      spacing={1}
      sx={
        hasError
          ? {
              borderWidth: 1,
              borderColor: "error.main",
              borderStyle: "solid",
              borderRadius: 1,
              padding: 2,
            }
          : {}
      }
    >
      <Typography variant="subtitle1" gutterBottom>
        Custom recurrence settings
      </Typography>
      <Stack direction="row" spacing={2}>
        <CustomRepeatEvery
          label="Every"
          value={customRepeat?.every}
          onChange={(value) => setCustomRepeat((prev) => ({ ...prev, every: value }))}
          disabled={loading}
          slotProps={{
            htmlInput: {
              "data-analytics-id": "create-event-form-custom-repeat-every-input",
            },
          }}
        />
        <CustomRepeatPeriod
          label="Period"
          value={customRepeat?.period}
          onChange={(value) => setCustomRepeat((prev) => ({ ...prev, period: value }))}
          disabled={loading}
          slotProps={{
            input: {
              "data-analytics-id": "create-event-form-custom-repeat-period-select",
            },
          }}
        />

        <CustomRepeatEnds onChange={handleCustomRepeatChange} disabled={loading} />
      </Stack>
      {hasError && (
        <Typography color="error.main" variant="caption">
          Please fill in all fields
        </Typography>
      )}
      {data && <CustomRepeatSumamry repeatInfo={data} />}
    </Stack>
  );
};
